import i18n from '@/utils/i18n';
import { Audio } from 'expo-av';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';

const chimeSound = require('@assets/audio/chime.mp3');

const LOADING_STEP_DURATIONS_MS = [12000, 15000, 15000, 10000, 8000];

export interface UsePricingTableLoadingStateParams {
  error: unknown;
  loading: boolean;
}

interface UsePricingTableLoadingStateResult {
  alertMe: boolean;
  loadingState: number;
  onAlertMePress: () => Promise<void>;
}

export const usePricingTableLoadingState = ({
  error,
  loading,
}: UsePricingTableLoadingStateParams): UsePricingTableLoadingStateResult => {
  const { showToast } = useExperienceToasts();
  const [alertMe, setAlertMe] = useState(false);
  const [loadingState, setLoadingState] = useState(1);
  const soundRef = useRef<Audio.Sound | null>(null);

  useEffect(() => {
    return () => {
      const unload = async () => {
        const sound = soundRef.current;
        if (!sound) {
          return;
        }
        const status = await sound.getStatusAsync();
        if (status.isLoaded) {
          await sound.unloadAsync();
        }
      };
      unload();
    };
  }, []);

  useEffect(() => {
    if (!loading && !error && alertMe) {
      soundRef.current?.replayAsync().catch(() => {
        // Do nothing
      });
      setAlertMe(false);
    }
  }, [loading, error, alertMe]);

  useEffect(() => {
    if (!loading || loadingState >= LOADING_STEP_DURATIONS_MS.length) {
      return;
    }

    const timer = setTimeout(
      () => {
        setLoadingState((prev) => prev + 1);
      },
      LOADING_STEP_DURATIONS_MS[loadingState - 1],
    );

    return () => clearTimeout(timer);
  }, [loading, loadingState]);

  const onAlertMePress = useCallback(async () => {
    const nextAlertMe = !alertMe;
    setAlertMe(nextAlertMe);
    showToast({
      title: i18n.t(
        `pricing.table.loading.alert_me.toast.${nextAlertMe ? 'on' : 'off'}`,
      ),
      variant: 'info',
    });

    if (!nextAlertMe || soundRef.current) {
      return;
    }
    try {
      const { sound } = await Audio.Sound.createAsync(chimeSound);
      soundRef.current = sound;
    } catch {
      // Do nothing
    }
  }, [alertMe, showToast]);

  return { alertMe, loadingState, onAlertMePress };
};
